import { get } from 'es-toolkit/compat';

/**
 * Transforms RJSF's internal path format to form data path format.
 */
export const transformRJSFPathToFormPath = (rjsfId: string): string => {
  if (!rjsfId.startsWith('root_')) {
    return rjsfId;
  }

  // Remove "root_arguments_" prefix first, then process array indices and underscores
  return rjsfId
    .replace(/^root_arguments_/, '')
    .replace(/_(\d+)_/g, '[$1].')
    .replace(/^(\d+)_/, '[$1].')
    .replace(/_/g, '.');
};

/**
 * Extracts image field value from RJSF formContext.
 */
export const getImageFieldValue = (formContext: unknown, imageFieldPath: string): unknown => {
  const context = formContext as Record<string, unknown>;

  if (!context?.flavors || context?.currentFlavorIndex === undefined) {
    return null;
  }

  const flavors = context.flavors as Array<Record<string, unknown>>;
  const currentFlavorIndex = context.currentFlavorIndex as number;
  const currentFlavor = flavors[currentFlavorIndex];
  const content = currentFlavor?.content as Record<string, unknown>;
  const formData = content?.arguments as Record<string, unknown>;

  if (!formData) return null;

  const result = get(formData, imageFieldPath);
  return result === undefined ? null : result;
};

/**
 * Derives the image field path from the alt text field path.
 */
export const deriveImageFieldPath = (altTextFieldId: string, uiSchema?: Record<string, unknown>): string => {
  // Use ui:options specified path if it's available (including empty string)
  const uiOptions = uiSchema?.['ui:options'] as Record<string, unknown> | null | undefined;
  if (uiOptions && 'imageFieldPath' in uiOptions) {
    return uiOptions.imageFieldPath as string;
  }

  // Transform the path and check if it ends with .altText
  const transformedPath = transformRJSFPathToFormPath(altTextFieldId);
  if (transformedPath.endsWith('.altText')) {
    return transformedPath.replace(/\.altText$/, '.src');
  }

  // Fallback: replace AltText with Src or altText with src in the original string
  if (altTextFieldId.endsWith('AltText')) {
    return altTextFieldId.replace(/AltText$/, 'Src');
  }
  return altTextFieldId.replace(/altText$/, 'src');
};
